/* eslint-disable no-else-return */
import { Form, Select } from "antd";
import { isArray, isEmpty, isFunction, sortBy, uniq, uniqBy } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../amplify/authenticator-provider";
import useFetchUsers from "../../hooks/useFetchUsers";

// Helper to get user display name - handles both fullName and firstName/lastName
const getUserDisplayName = user => {
  if (!user) return "";
  if (user.fullName) return user.fullName;
  if (user.firstName || user.lastName) {
    return `${user.firstName || ""} ${user.lastName || ""}`.trim();
  }
  return "";
};

export default function UserSelector({
  className,
  placeholder,
  handleUsersSelection = () => {},
  allowClear = false,
  value,
  containerClassName = "w-full",
  name = "",
  showAll = true,
  multiple = true,
  disabled = false,
  errorMessage,
  validation = false,
  item = {},
  maxTagCount = "responsive",
  required = true,
  allEmptyArray = false,
  maxSelection,
  resetDefaultUser = false,
  size = "middle",
  extraQueryParams = {}, // make sure to pass supported params only
  ignoreCache = false, // When true, skip cache and always fetch fresh from API
  onUsersLoaded, // Optional callback fired with the fetched user list
  syncOnLocationChange = false, // Opt-in: re-emit fresh IDs when "All" is active and the user list refetches (location/company change). Off by default to keep existing callers unchanged.
  defaultAllSelected = false, // Opt-in: render the "All" chip initially when no value is set. Other callers stay on the placeholder behavior they had before.
}) {
  const [userSearch, setuserSearch] = useState("");
  const { user } = useContext(UserContext);
  const [isAllSelected, setisAllSelected] = useState(defaultAllSelected);
  const [userList, setuserList] = useState([]);
  const [previousUserIds, setpreviousUserIds] = useState([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const companyId = user?.company?.id ? parseInt(user.company.id) : null;
  const currentLocationId = sessionStorage.getItem("currentLocationId") || "";
  const prevLocationRef = useRef(currentLocationId);

  // Fetch users - Apollo handles caching based on variables (companyId, locationIds, etc.)
  // When variables change (e.g. location switch), Apollo auto-fetches fresh data
  const handleFetchComplete = res => {
    try {
      const fetchedUsers = res?.getUsers?.data || [];
      // Use functional setState to avoid stale closure — prevList is always the latest state
      setuserList(prevList => {
        return sortBy(
          uniqBy([...prevList, ...fetchedUsers], u => String(u.id)),
          u => getUserDisplayName(u)
        );
      });
      setpreviousUserIds(value);
      setInitialDataLoaded(true);
      // Keep "All" semantics across location switches: if the user previously
      // chose "All", the parent's value is a stale snapshot of the OLD
      // location's user IDs. Re-emit the new location's IDs so the payload
      // stays consistent with what the dropdown displays. Opt-in via prop
      // so unrelated callers are unaffected.
      if (syncOnLocationChange && multiple && isAllSelected && fetchedUsers.length > 0) {
        const freshIds = fetchedUsers.map(u => +u.id);
        if (allEmptyArray) {
          handleUsersSelection([]);
        } else {
          handleUsersSelection(freshIds);
        }
      }
      if (isFunction(onUsersLoaded)) onUsersLoaded(fetchedUsers);
    } catch (error) {
      console.error("Error processing user data:", error);
      setInitialDataLoaded(true);
    }
  };

  const { loading: userLoader, refetch: fetchUsers } = useFetchUsers(
    null,
    handleFetchComplete,
    !companyId, // skip only when companyId is not available
    {
      isCurrentLocationUser: true,
      ...(ignoreCache && { fetchPolicy: "no-cache" }),
      ...extraQueryParams,
    }
  );

  // When location changes within the same company, refetch users for the new location
  useEffect(() => {
    if (prevLocationRef.current !== currentLocationId && companyId) {
      setuserList([]); // Clear old location's users before fetching new ones
      fetchUsers();
    }
    prevLocationRef.current = currentLocationId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocationId, companyId]);

  // Load initial user data from item prop (for pre-populating assigned user)
  // Only append user if they're MISSING in userList
  useEffect(() => {
    try {
      const userListIds = userList.map(u => String(u.id));
      let usersToAppend = [];

      // Only add users from item.item.user if they're MISSING in userList
      if (!isEmpty(item?.item)) {
        if (isArray(item.item.user)) {
          usersToAppend = item.item.user.filter(
            u => u && u.id && !userListIds.includes(String(u.id))
          );
        } else if (item.item.user && item.item.user.id) {
          if (!userListIds.includes(String(item.item.user.id))) {
            usersToAppend = [item.item.user];
          }
        }
      }

      // Append missing users to userList
      if (usersToAppend.length > 0) {
        const mergedUsers = sortBy([...userList, ...usersToAppend], u => getUserDisplayName(u));
        setuserList(mergedUsers);
      }
    } catch (error) {
      console.error("Error loading initial user data:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, userList.length]);

  useEffect(() => {
    try {
      if (multiple && isArray(value) && userList.length > 0) {
        const sortedUsers = [...userList]?.sort((a, b) => {
          // Check if the current user's id exists in userIds
          const aMatch = value?.includes(Number(a?.id));
          const bMatch = value?.includes(Number(b?.id));

          // Sort matched ids to the front
          if (aMatch && !bMatch) return -1;
          if (!aMatch && bMatch) return 1;
          return 0;
        });

        // Only update state if the order actually changed
        const orderChanged = sortedUsers.some((user, idx) => user.id !== userList[idx]?.id);
        if (orderChanged) {
          setuserList(sortedUsers);
        }
      }
    } catch (error) {
      console.error("Error sorting user list:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, multiple]);

  const resetSearch = () => {
    setuserSearch("");
  };

  const handleUsers = e => {
    try {
      if (multiple) {
        if (e.includes("all")) {
          setisAllSelected(true);
          if (allEmptyArray) {
            handleUsersSelection([]);
          } else {
            handleUsersSelection(userList.map(item => +item.id));
          }
        } else {
          const userIds = resetDefaultUser ? [] : previousUserIds;

          handleUsersSelection(uniq([...userIds, ...e.map(item => +item)]));
        }
        setpreviousUserIds([]);
      } else {
        handleUsersSelection(e);
      }
      resetSearch();
    } catch (err) {
      console.log(err);
    }
  };
  const handleFilter = (inputValue, option) => {
    try {
      const child0 = option?.children?.[0] || "";
      const child2 = option?.children?.[2] || "";
      return `${child0} ${child2}`.toLowerCase().includes(inputValue?.toLowerCase() || "");
    } catch (error) {
      console.error("Error filtering options:", error);
      return true;
    }
  };
  const maxDisabled = id => {
    try {
      if (maxSelection && isArray(value)) {
        if (value.includes(+id)) {
          return false;
        } else if (value.length >= maxSelection) {
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("Error checking max disabled:", error);
      return false;
    }
  };

  // Check if the current value exists in userList
  const hasValueInUserList = () => {
    if (!value) return true;
    if (multiple) {
      return isArray(value) && value.every(id => userList.some(u => +u.id === +id));
    } else {
      return userList.some(u => +u.id === +value);
    }
  };

  // Determine the value to display - ensure values are numbers to match Select.Option values
  const getDisplayValue = () => {
    if (!multiple) {
      // For single select: only show undefined if we're loading with empty userList
      if (userLoader && !initialDataLoaded && userList.length === 0 && !hasValueInUserList()) {
        return undefined;
      }
      // Convert to number to match Select.Option value={+item?.id}
      return value != null ? +value : value;
    } else {
      // For multiple select
      if (isAllSelected) return "all";
      if (userLoader && !initialDataLoaded && userList.length === 0 && !hasValueInUserList()) {
        return undefined;
      }
      // Convert array values to numbers to match Select.Option value={+item?.id}
      return isArray(value) ? value.map(v => +v) : value;
    }
  };

  return (
    <div className={containerClassName}>
      {validation ? (
        <Form.Item
          rules={[
            { required: required, message: errorMessage || `Please select ${item.name} user` },
          ]}
          name={name || `leadOpportunityUsers-${item.id}`}
          label={<span className="form-field-label">{item.name}</span>}
        >
          <Select
            showSearch
            // onSearch={e => setuserSearch(e)}
            name={name || `leadOpportunityUsers-${item.id}`}
            disabled={disabled}
            allowClear={allowClear}
            filterOption={handleFilter}
            mode={multiple ? "multiple" : ""}
            loading={userLoader}
            maxTagCount={maxTagCount}
            value={getDisplayValue()}
            onClear={() => handleUsersSelection(null)}
            placeholder={placeholder || "Select users"}
            className={className || "w-full"}
            size={size}
            onChange={e => {
              if (e?.length === 0) {
                setisAllSelected(prev => !prev);
              }
              handleUsers(e);
            }}
          >
            {showAll && <Select.Option value="all">All</Select.Option>}
            {sortBy(userList, user => getUserDisplayName(user)).map((item, index) => (
              <Select.Option
                disabled={(multiple ? isAllSelected : false) || userLoader || maxDisabled(item?.id)}
                value={+item?.id}
                key={index}
              >
                {getUserDisplayName(item)}{" "}
                {!isEmpty(item?.companyRole) ? `(${item?.companyRole?.name})` : ""}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      ) : (
        <Select
          showSearch
          // onSearch={e => setuserSearch(e)}
          name={name}
          disabled={disabled}
          allowClear={allowClear}
          filterOption={handleFilter}
          mode={multiple ? "multiple" : ""}
          loading={userLoader}
          maxTagCount={maxTagCount}
          value={getDisplayValue()}
          onClear={() => handleUsersSelection(null)}
          placeholder={placeholder || "Select users"}
          className={className || "w-full"}
          onChange={e => {
            if (e?.length === 0) {
              setisAllSelected(prev => !prev);
            }
            handleUsers(e);
          }}
          size={size}
        >
          {showAll && <Select.Option value="all">All</Select.Option>}
          {((value || []).length > 0 ? userList : sortBy(userList, u => getUserDisplayName(u))).map(
            (item, index) => (
              <Select.Option
                disabled={(multiple ? isAllSelected : false) || userLoader || maxDisabled(item?.id)}
                value={+item?.id}
                key={index}
              >
                {getUserDisplayName(item)}{" "}
                {!isEmpty(item?.companyRole) ? `(${item?.companyRole?.name})` : ""}
              </Select.Option>
            )
          )}
        </Select>
      )}
    </div>
  );
}
